import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { recaptchaHeaders } from "@/lib/recaptcha";
import { setLastAuthMethod, clearLastAuthMethod } from "@/lib/lastAuthMethod";

const AuthContext = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/auth/me");
            setUser(r.data.user);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        // If returning from OAuth callback, skip /me — AuthCallback will exchange session_id first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    const loginWithEmail = async (email, password, options = {}) => {
        const headers = await recaptchaHeaders("login");
        const body = { email, password };
        if (options.trustDevice) {
            body.trust_device = true;
        }
        const r = await api.post("/auth/login", body, { headers });
        setUser(r.data.user);
        setLastAuthMethod("email");
        return r.data.user;
    };

    const registerWithEmail = async (payload) => {
        const headers = await recaptchaHeaders("signup");
        const r = await api.post("/auth/register", payload, { headers });
        setUser(r.data.user);
        setLastAuthMethod("email");
        return r.data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch { /* non-critical */ }
        try {
            const { track } = await import("@/lib/analytics");
            track("auth_logout");
        } catch { /* non-critical */ }
        clearLastAuthMethod();
        setUser(null);
    };

    const signInWithGoogle = (redirectPath = "/dashboard") => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        setLastAuthMethod("google");
        const redirectUrl = window.location.origin + redirectPath;
        window.location.href =
            "https://auth.emergentagent.com/?redirect=" +
            encodeURIComponent(redirectUrl);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                refresh,
                loginWithEmail,
                registerWithEmail,
                logout,
                signInWithGoogle,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
